import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import * as enrollmentActions from '../../../actions/enrollmentActions'
import { dropModalFor } from '../../../utility/enrollmentsBilling'
import { reset as resetDualPanelData } from '../../../reducers/dualPanelSelectorReducer'

import useDropActions from './useDropActions'
import useInvoiceActions from './useInvoiceActions'

// Every enrollment action a coach can take from a detail view: approve, drop (with or without a
// refund), transfer, delete, and the invoice lifecycle. Owns the modal state and returns ready-made
// prop bundles, so any surface that shows an enrollment gets the same flows without re-wiring them.
//
// Used by the Enrollments & Billing drawer and by the Members drawer's Enrollments tab. Extracted
// so the two share one implementation of the money-moving paths rather than a copy each.
const useEnrollmentActions = ({ listEnrollment, enrollment, onActionComplete, loadEnrollment, onRemoved }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [activeModal, setActiveModal] = useState(null) // 'approve' | 'drop' | 'remove' | 'delete' | invoice modals
  const [actionLoading, setActionLoading] = useState(false)
  const [coachMessage, setCoachMessage] = useState('')
  const [notifyViaEmail, setNotifyViaEmail] = useState(true)

  // Transfer flow (reuses the waitlist program selector)
  const [showTransfer, setShowTransfer] = useState(false)
  const [showTransferConfirm, setShowTransferConfirm] = useState(false)
  const [selectedCourse, setSelectedCourse] = useState(null)
  const [showTransferSuccess, setShowTransferSuccess] = useState(false)

  const failed = () => toast.error(t('enrollmentsBilling.drawer.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT })

  const closeModal = () => {
    setActiveModal(null)
    setCoachMessage('')
    setNotifyViaEmail(true)
  }

  const handleApprove = () => {
    setActionLoading(true)
    dispatch(enrollmentActions.updateEnrollment({ enrollmentData: { id: listEnrollment.id, status: 'active' }, coachMessage })).unwrap()
      .then(() => {
        toast.success(t('enrollmentsBilling.drawer.enrollmentApproved'), { position: toast.POSITION.TOP_RIGHT })
        closeModal()
        onActionComplete?.()
        loadEnrollment()
      })
      .catch(failed)
      .finally(() => setActionLoading(false))
  }

  // "Delete" (and the pending "Decline") soft-deletes the enrollment via the deleteEnrollment thunk:
  // DELETE /api/v1/enrollments/:id. The record is discarded and leaves the list. For online-paid
  // enrollments the refund flag is passed through (RefundEnrollmentModal), mirroring the roster drop.
  const handleRemoveEnrollment = (refund = false) => {
    const wasActive = (enrollment || listEnrollment)?.status === 'active'
    setActionLoading(true)
    dispatch(enrollmentActions.deleteEnrollment({ enrollmentId: listEnrollment.id, refund, coachMessage, notifyViaEmail })).unwrap()
      .then(() => {
        const key = wasActive ? 'enrollmentDeleted' : 'enrollmentDeclined'
        toast.success(t(`enrollmentsBilling.drawer.${key}`), { position: toast.POSITION.TOP_RIGHT })
        closeModal()
        onActionComplete?.()
        // The record is gone, so the host view has to leave it: the drawer closes, the Members tab
        // returns to the enrollment list.
        onRemoved?.()
      })
      .catch(failed)
      .finally(() => setActionLoading(false))
  }

  const invoiceActions = useInvoiceActions({ enrollment, setActiveModal, setActionLoading, onActionComplete, loadEnrollment })

  const dropActions = useDropActions({
    listEnrollment, enrollment, coachMessage, notifyViaEmail, setActionLoading, closeModal, onActionComplete, loadEnrollment,
    refundOnly: invoiceActions.handleRefund,
  })

  // Flip the enrollment's payment status directly (offline paid <-> outstanding). No invoice is
  // touched — this is only for enrollments tracked by payment_status alone.
  const handleMarkPaymentStatus = (paymentStatus) => {
    setActionLoading(true)
    dispatch(enrollmentActions.updateEnrollment({ enrollmentData: { id: listEnrollment.id, paymentStatus } })).unwrap()
      .then(() => {
        const key = paymentStatus === 'paid' ? 'enrollmentsBilling.drawer.markedPaidStatus' : 'enrollmentsBilling.drawer.markedOutstandingStatus'
        toast.success(t(key), { position: toast.POSITION.TOP_RIGHT })
        onActionComplete?.()
        loadEnrollment()
      })
      .catch(failed)
      .finally(() => setActionLoading(false))
  }

  const closeTransfer = () => {
    setShowTransfer(false)
    setShowTransferConfirm(false)
    setSelectedCourse(null)
    dispatch(resetDualPanelData())
  }

  const handleTransfer = ({ coachMessage: message, sendEmail }) => {
    if (!selectedCourse) { return }

    setActionLoading(true)
    dispatch(enrollmentActions.transferEnrollment({
      enrollmentId: listEnrollment.id, courseId: selectedCourse.id, coachMessage: message, sendEmail,
    })).unwrap()
      .then(() => {
        closeTransfer()
        onActionComplete?.()
        loadEnrollment()
        setShowTransferSuccess(true)
      })
      .catch(failed)
      .finally(() => setActionLoading(false))
  }

  const actionModalProps = {
    activeModal,
    closeModal,
    coachMessage,
    setCoachMessage,
    notifyViaEmail,
    toggleNotify: () => setNotifyViaEmail(notify => !notify),
    isLoading: actionLoading,
    onApprove: handleApprove,
    onDrop: dropActions.handleDrop,
    onDropNoRefund: dropActions.handleDropNoRefund,
    onDropRefund: dropActions.handleDropRefund,
    onRemove: () => handleRemoveEnrollment(false),
    dropEnrollment: enrollment || listEnrollment,
    invoice: enrollment?.invoice,
    dropSuccessTitle: dropActions.dropSuccessTitle,
    closeDropSuccess: dropActions.closeDropSuccess,
  }

  const transferModalProps = {
    enrollment,
    showTransfer,
    showTransferConfirm,
    showTransferSuccess,
    selectedCourse,
    onSelectProgram: setSelectedCourse,
    closeTransfer,
    onProceedToConfirm: () => { setShowTransfer(false); setShowTransferConfirm(true) },
    onConfirmTransfer: handleTransfer,
    closeTransferSuccess: () => setShowTransferSuccess(false),
    isLoading: actionLoading,
  }

  const invoiceModalProps = {
    activeModal,
    closeModal,
    enrollment,
    invoice: enrollment?.invoice,
    actionLoading,
    handleMarkPaid: invoiceActions.handleMarkPaid,
    handleRefund: invoiceActions.handleRefund,
    handleEditInvoice: invoiceActions.handleEditInvoice,
    handleCreateInvoice: invoiceActions.handleCreateInvoice,
    handleMarkOutstanding: invoiceActions.handleMarkOutstanding,
    showMarkPaidSuccess: invoiceActions.showMarkPaidSuccess,
    setShowMarkPaidSuccess: invoiceActions.setShowMarkPaidSuccess,
    markPaidEmailSent: invoiceActions.markPaidEmailSent,
    showReminderSuccess: invoiceActions.showReminderSuccess,
    setShowReminderSuccess: invoiceActions.setShowReminderSuccess,
  }

  return {
    actionLoading,
    openApprove: () => setActiveModal('approve'),
    openDecline: () => setActiveModal('remove'),
    openDrop: () => setActiveModal(dropModalFor(enrollment || listEnrollment)),
    openDelete: () => setActiveModal('delete'),
    openTransfer: () => setShowTransfer(true),
    invoiceHandlers: invoiceActions.invoiceHandlers,
    handleMarkPaymentStatus,
    actionModalProps,
    transferModalProps,
    invoiceModalProps,
  }
}

export default useEnrollmentActions
